import { Typography } from 'beek-design-system';
import styled from 'styled-components';
import useWindowSize from 'beek-design-system/utils/useWindowSize';

import {
  FallbackSourceType,
  ImageSourceType,
  getCurrentImageDimensions,
  getImageFallback,
  getImageSources,
} from './pictureHelper';
import { ImageConfigSizeType, UrlConfigSize } from './imageConfigSizes';
import fallbacks from './imageFallbacks';

const { audioCoverFallback } = fallbacks;

export type ImageProps = {
  url: string;
  laptopUrl?: string;
  fallbackUrl?: string;
  width?: number;
  height?: number;
  alt?: string;
  credits?: string;
  type?: string;
  sizeConfig?: ImageConfigSizeType;
  urlsConfig?: UrlConfigSize;
  marginTop?: string;
  marginBottom?: string;
  marginRight?: string;
  marginLeft?: string;
  lazyLoad?: boolean;
  border?: boolean;
  borderRadius?: boolean;
  quality?: number;
  crop?: string;
};

type ImagePictureProps = {
  marginTop?: string;
  marginBottom?: string;
  marginRight?: string;
  marginLeft?: string;
  type?: string;
};

type StyledImageProps = {
  alt?: string;
  src?: string;
  crop?: string;
  srcset?: string;
  loading?: string;
  type?: string;
  laptopUrl?: string;
  border?: boolean;
  borderRadius?: boolean;
};

const defaultProps = {
  type: null,
  width: null,
  height: null,
  laptopUrl: '',
  sizeConfig: null,
  urlsConfig: null,
  alt: 'Beek image',
  credits: null,
  fallbackUrl: null,
  marginTop: null,
  marginBottom: null,
  marginRight: null,
  marginLeft: null,
  lazyLoad: true,
  border: false,
  borderRadius: false,
  quality: 75,
  crop: 'cover',
};

const PictureContainer = styled.picture<ImagePictureProps>`
  ${({ marginTop }): string => (marginTop ? `margin-top: ${marginTop}` : '')};
  ${({ marginBottom }): string =>
    marginBottom ? `margin-bottom: ${marginBottom}` : ''};
  ${({ marginRight }): string =>
    marginRight ? `margin-right: ${marginRight}` : ''};
  ${({ marginLeft }): string =>
    marginLeft ? `margin-left: ${marginLeft}` : ''};
`;

const StyledImage = styled.img<StyledImageProps>`
  border: ${({ border }): string =>
    border ? '0.063rem solid var(--gray-15) !important' : 'none'};
  border-radius: ${({ borderRadius }): string =>
    borderRadius ? '0.5rem !important' : '0rem'};
  object-fit: ${({ crop }): string => crop || 'cover'};
`;

const Image = (props: ImageProps): JSX.Element => {
  const screenSize = useWindowSize();

  let { url } = props;
  const {
    alt,
    type,
    credits,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    lazyLoad,
    laptopUrl,
    fallbackUrl,
    border,
    borderRadius,
    sizeConfig,
    urlsConfig,
    width,
    height,
    crop,
    quality,
  } = props;

  url = url !== '' ? url : audioCoverFallback;

  const imgSources: ImageSourceType[] = getImageSources({
    url,
    type,
    laptopUrl,
    width,
    height,
    sizeConfig,
    urlsConfig,
    crop,
    quality,
  });
  const fallback: FallbackSourceType = getImageFallback({
    fallbackUrl,
    type,
    width,
    height,
    sizeConfig,
    crop,
    quality,
  });

  const renderPictureSources = (): JSX.Element[] =>
    imgSources.map(
      (source: ImageSourceType): JSX.Element => (
        <source key={source.key} media={source.media} srcSet={source.srcset} />
      )
    );
  const renderCredits = (): JSX.Element | null => {
    if (credits) {
      return (
        <Typography
          display="block"
          tag="p"
          type="caption"
          colorVariant="gray50"
        >
          {credits}
        </Typography>
      );
    }
    return null;
  };

  const [imgWidth, imgHeigth] = getCurrentImageDimensions({
    type,
    width,
    height,
    sizeConfig,
    currentWidth: screenSize.width,
  });

  return (
    <PictureContainer
      marginTop={marginTop}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      marginRight={marginRight}
      type={type}
    >
      {renderPictureSources()}
      <StyledImage
        alt={alt}
        src={fallback.src}
        srcset={fallback.srcset}
        loading={lazyLoad ? 'lazy' : 'eager'}
        border={border}
        borderRadius={borderRadius}
        height={imgHeigth}
        width={imgWidth}
      />
      {renderCredits()}
    </PictureContainer>
  );
};

Image.defaultProps = defaultProps;

export default Image;
